import styled from '@emotion/styled'
import * as React from 'react'

import { TButtonVariantProps } from '~ui/components/buttons/Button/buildButtonVariantProps'
import { Button } from '~ui/components/buttons/Button/Button'
import { Icon } from '~ui/components/icons/Icon'
import { TSpace } from '~ui/styleGuide/spaces'
import { TChakraPositionProps } from '~ui/typings/chakra'

type TButtonProps = TChakraPositionProps &
  Pick<
    PropsOf<typeof Button>,
    | 'size'
    | 'type'
    | 'shadow'
    | 'radius'
    | 'onClick'
    | 'state'
    | 'marginLeft'
    | 'marginRight'
    | 'width'
    | 'height'
    | 'className'
    // TODO tooltip
    | 'title'
    | 'marginTop'
    | 'marginBottom'
  >

type TStyledProps = {
  /**
   * correct the icon position if needed
   * positive value = move in said direction
   * eg. `{ up: 2 }` will move the icon 2px up
   */
  moveIconPx?: {
    up?: number
    down?: number
    left?: number
    right?: number
  }
}

type TProps = NoChildren &
  TestingProps &
  TButtonProps &
  TButtonVariantProps &
  TStyledProps & {
    /**
     * component, usually imported from `react-icons`
     */
    icon: PropsOf<typeof Icon>['icon']
    /**
     * CSS value with unit, like `1.5rem`
     */
    iconSize?: PropsOf<typeof Icon>['iconSize']
    /**
     * how contrasted the icon should be to the background
     */
    iconContrast?: PropsOf<typeof Icon>['contrast']
    /**
     * key of projects colors, for deeper values use array: `specialColors.company`
     * color of the icon, use only if you need to set colors of both the button background/outline and the icon
     */
    iconColor?: PropsOf<typeof Icon>['color']
    /**
     * spacing around the icon inside the button
     */
    padding?: TSpace
  }

// get rid of the React error when passing unknown prop to the HTML element
const propForwardOptions = {
  shouldForwardProp: (propName: string) => propName !== `moveIconPx`,
}

const StyledIcon = styled(Icon, propForwardOptions)<TStyledProps>`
  position: relative;

  top: ${({ moveIconPx }) => (moveIconPx?.up ? `${-1 * moveIconPx.up}px` : `unset`)};
  bottom: ${({ moveIconPx }) => (moveIconPx?.down ? `${-1 * moveIconPx.down}px` : `unset`)};
  left: ${({ moveIconPx }) => (moveIconPx?.left ? `${-1 * moveIconPx.left}px` : `unset`)};
  right: ${({ moveIconPx }) => (moveIconPx?.right ? `${-1 * moveIconPx.right}px` : `unset`)};
`

/**
 * Button with just icon inside it – use for clickable icons
 */
export const ButtonIcon: React.FC<TProps> = ({
  icon,
  padding,
  moveIconPx,
  iconSize = `2rem`,
  iconColor,
  iconContrast,
  dataTestId,
  ...buttonProps
}) => {
  const iconColorOrDisabled: PropsOf<typeof Icon>['color'] =
    buttonProps.state === `disabled` || buttonProps.state === `blocked` ? `currentColor` : iconColor

  return (
    <Button {...buttonProps} dataTestId={dataTestId} padding={padding}>
      <StyledIcon
        icon={icon}
        iconSize={iconSize}
        contrast={iconContrast}
        moveIconPx={moveIconPx}
        color={iconColorOrDisabled}
      />
    </Button>
  )
}
